'use client';
import React, { useState, useEffect } from 'react';
import PublicTopbar from '@/components/PublicTopbar';
import PublicFooter from '@/app/components/PublicFooter';
import AppImage from '@/components/ui/AppImage';
import { MapPin, Clock, Ticket, Calendar, Globe, Loader2 } from 'lucide-react';
import Link from 'next/link';

type EventStatus = 'CONFIRMED' | 'UPCOMING' | 'SOLD_OUT' | 'PAST';

interface Event {
  id: string;
  title: string;
  type: string;
  date: string;
  day: string;
  month: string;
  time: string;
  venue: string;
  city: string;
  country: string;
  flag: string;
  status: EventStatus;
  ticketLink: string;
  ticketPrice: string;
  image: string;
  imageAlt: string;
  description: string;
  isPast: boolean;
}

const statusConfig: Record<EventStatus, { label: string; className: string }> = {
  CONFIRMED: { label: 'Confirmed', className: 'status-confirmed' },
  UPCOMING: { label: 'Upcoming', className: 'status-scheduled' },
  SOLD_OUT: { label: 'Sold Out', className: 'status-cancelled' },
  PAST: { label: 'Past', className: 'status-completed' },
};

export default function EventsPage() {
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/events')
      .then((r) => r.json())
      .then((data) => setEvents(data.events ?? []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const upcoming = events.filter((e) => !e.isPast);
  const past = events.filter((e) => e.isPast);
  const displayed = tab === 'upcoming' ? upcoming : past;

  return (
    <div className="min-h-screen bg-background">
      <PublicTopbar />

      {/* Header */}
      <section className="pt-32 pb-12 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Live</p>
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <h1 className="text-5xl font-800 text-foreground">Events</h1>
            <div className="flex items-center gap-1 bg-muted/40 rounded-xl p-1">
              <button
                onClick={() => setTab('upcoming')}
                className={`px-4 py-2 text-sm font-600 rounded-lg transition-all ${tab === 'upcoming' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                Upcoming ({upcoming.length})
              </button>
              <button
                onClick={() => setTab('past')}
                className={`px-4 py-2 text-sm font-600 rounded-lg transition-all ${tab === 'past' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                Past Events ({past.length})
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Events list */}
      <section className="pb-24 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-24">
              <Calendar size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No {tab} events at this time.</p>
              <p className="text-sm text-muted-foreground mt-1">Check back soon or subscribe to the newsletter.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {displayed.map((event, i) => (
                <div
                  key={event.id}
                  className={`group relative rounded-2xl overflow-hidden bg-card border border-border card-glow-hover transition-all duration-300 ${i === 0 && tab === 'upcoming' ? 'ring-1 ring-primary/30' : ''}`}>
                  {i === 0 && tab === 'upcoming' && (
                    <div className="absolute top-4 right-4 z-10 px-3 py-1 rounded-full bg-primary/90 text-primary-foreground text-[10px] font-700 tracking-widest uppercase">
                      Next Show
                    </div>
                  )}
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
                    {/* Image */}
                    <div className="lg:col-span-4 relative h-48 lg:h-auto overflow-hidden">
                      <AppImage
                        src={event.image}
                        alt={event.imageAlt}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-700"
                        sizes="(max-width: 1024px) 100vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/60 hidden lg:block" />
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-8 p-6 lg:p-8 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className={`px-2.5 py-1 text-[10px] font-600 rounded-full ${statusConfig[event.status]?.className ?? 'status-scheduled'}`}>
                            {statusConfig[event.status]?.label ?? event.status}
                          </span>
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">{event.type}</span>
                        </div>
                        <h2 className="text-2xl font-700 text-foreground mb-3">{event.title}</h2>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-4">{event.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Calendar size={14} className="text-primary" />
                            {event.date}
                          </span>
                          {event.time && (
                            <span className="flex items-center gap-1.5">
                              <Clock size={14} className="text-primary" />
                              {event.time}
                            </span>
                          )}
                          <span className="flex items-center gap-1.5">
                            <MapPin size={14} className="text-primary" />
                            {event.venue}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Globe size={14} className="text-primary" />
                            {event.flag} {event.city}, {event.country}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Tickets from</p>
                          <p className="text-lg font-700 text-primary font-tabular">{event.ticketPrice}</p>
                        </div>
                        {!event.isPast && (
                          <Link
                            href="/booking"
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-gold text-primary-foreground text-sm font-700 hover:opacity-90 active:scale-95 transition-all">
                            <Ticket size={16} />
                            Get Tickets
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}